import { Modal, Form, Input, Tabs, Typography, Alert } from "antd";
import { useState } from "react";

import {
  PCD_FORM_MODES,
  PCD_LANGUAGE_CONFIG,
  PCD_LANGUAGE_ENUM,
  PCD_SNIPPET_MODAL_TEXT
} from "../helper";

const { TextArea } = Input;
const { Text } = Typography;

/**
 * Builds initial implementations map from existing snippet data for editing.
 * @param {Array} implementations - Existing implementations array [{language, code}]
 * @returns {Object} Map of language key to code string
 */
const buildCodeSnippetsMap = (implementations = []) => {
  const snippetCodeMap = {};
  for (const languageConfig of PCD_LANGUAGE_CONFIG) {
    const existingSnippet = implementations.find((snippet) => snippet.language === languageConfig.key);
    snippetCodeMap[languageConfig.key] = existingSnippet?.code || "";
  }
  return snippetCodeMap;
};

/**
 * Converts the code snippets map into the API payload format.
 * Only includes languages that have non-empty code.
 * @param {Object} snippetCodeMap - { "C++": "code...", "Java": "code...", ... }
 * @returns {Array} [{language, code}, ...]
 */
const buildCodeSnippetsPayload = (snippetCodeMap) => {
  return Object.entries(snippetCodeMap)
    .filter(([, code]) => code.trim().length > 0)
    .map(([language, code]) => ({ language, code }));
};

const PcdSnippetFormModal = ({
  mode = PCD_FORM_MODES.CREATE,
  isModalVisible = false,
  onCancel = () => {},
  onSave = () => {},
  snippetData = null,
  isSubmitting = false
}) => {
  const [formInstance] = Form.useForm();
  const isEditMode = mode === PCD_FORM_MODES.EDIT;

  const [snippetCodeMap, setSnippetCodeMap] = useState(() =>
    buildCodeSnippetsMap(snippetData?.implementations)
  );

  const [activeLanguageTab, setActiveLanguageTab] = useState(PCD_LANGUAGE_ENUM.CPP);

  const handleCodeChange = (languageKey, codeValue) => {
    setSnippetCodeMap((previousMap) => ({
      ...previousMap,
      [languageKey]: codeValue
    }));
  };

  const handleSubmit = async () => {
    try {
      const { contestLink, problemName, description } = await formInstance.validateFields();

      // Auto-detect which languages have code
      const implementations = buildCodeSnippetsPayload(snippetCodeMap);

      if (implementations.length === 0) {
        formInstance.setFields([
          {
            name: "codeSnippetsValidator",
            errors: [PCD_SNIPPET_MODAL_TEXT.AT_LEAST_ONE_SNIPPET_ERROR]
          }
        ]);
        return;
      }

      const payload = {
        contestLink: contestLink.trim(),
        problemName: problemName.trim(),
        description: description?.trim() || null,
        implementations
      };

      onSave(payload);
    } catch {
      // Validation errors are shown by Ant Design Form
    }
  };

  const { contestLink, problemName, title, description } = snippetData || {};

  const initialFormValues = {
    contestLink: contestLink || "",
    problemName: problemName || title || "",
    description: description || ""
  };

  const languageTabItems = PCD_LANGUAGE_CONFIG.map((languageConfig) => {
    const isActive = activeLanguageTab === languageConfig.key;
    const currentCode = snippetCodeMap[languageConfig.key] || "";
    const hasCode = currentCode.trim().length > 0;

    return {
      key: languageConfig.key,
      label: (
        <span>
          {languageConfig.label}
          {languageConfig.isMandatory && (
            <Text type="danger" style={{ marginLeft: 4, fontSize: 12 }}>
              *
            </Text>
          )}
          {!languageConfig.isMandatory && hasCode && (
            <div
              style={{
                display: "inline-block",
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: "#52c41a",
                marginLeft: 6,
                marginBottom: 2
              }}
            />
          )}
        </span>
      ),
      children: (
        <div>
          <TextArea
            rows={12}
            value={currentCode}
            onChange={(event) => handleCodeChange(languageConfig.key, event.target.value)}
            placeholder={`Paste your ${languageConfig.label} code here...`}
            style={{
              fontFamily: "'Fira Code', 'Cascadia Code', 'Ubuntu Mono', Consolas, monospace",
              fontSize: "13px",
              backgroundColor: "#1e1e1e",
              color: "#d4d4d4",
              border: "1px solid #333",
              padding: "16px",
              lineHeight: "1.5",
              caretColor: "#fff"
            }}
          />
        </div>
      ),
      forceRender: isActive
    };
  });

  return (
    <Modal
      title={
        isEditMode
          ? `${PCD_SNIPPET_MODAL_TEXT.EDIT_TITLE}: ${snippetData?.problemName || ""}`
          : PCD_SNIPPET_MODAL_TEXT.CREATE_TITLE
      }
      open={isModalVisible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={isSubmitting}
      okText={isEditMode ? "Update" : "Create"}
      width={800}
      destroyOnClose
      centered
    >
      <Form form={formInstance} layout="vertical" initialValues={initialFormValues}>
        <Form.Item
          name="contestLink"
          label="Contest Link"
          rules={[
            { required: true, message: "Contest link is required" },
            { type: "url", message: "Please enter a valid URL" }
          ]}
        >
          <Input placeholder="https://codeforces.com/contest/2189" />
        </Form.Item>

        <Form.Item
          name="problemName"
          label="Problem Name"
          rules={[
            { required: true, message: "Problem name is required" },
            { max: 255, message: "Problem name must be at most 255 characters" }
          ]}
        >
          <Input placeholder="Two Sum" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description (Optional)"
          rules={[{ max: 2000, message: "Description must be at most 2000 characters" }]}
        >
          <TextArea rows={3} placeholder="Brief description of the problem..." />
        </Form.Item>

        <Form.Item
          name="codeSnippetsValidator"
          label="Code Snippets"
          rules={[
            {
              validator: () => {
                const hasAtLeastOneSnippet = Object.values(snippetCodeMap).some(
                  (code) => code?.trim().length > 0
                );
                if (!hasAtLeastOneSnippet) {
                  return Promise.reject(PCD_SNIPPET_MODAL_TEXT.AT_LEAST_ONE_SNIPPET_ERROR);
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Alert
            message="Provide code for at least one language. Any language you provide will be saved automatically."
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
          />
          <Tabs
            activeKey={activeLanguageTab}
            onChange={setActiveLanguageTab}
            items={languageTabItems}
            type="card"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PcdSnippetFormModal;
